import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '../../../shared/ui'
import { useCreateDraft, useProbearbeiterQueue, type ProbearbeiterRow } from './onboardingApi'

function avgRating(r: ProbearbeiterRow): number | null {
  const xs = [r.probe_rating_punktlichkeit, r.probe_rating_hygiene, r.probe_rating_teamfaehigkeit, r.probe_rating_engagement]
    .filter((x): x is number => typeof x === 'number')
  if (xs.length === 0) return null
  return xs.reduce((a, b) => a + b, 0) / xs.length
}

export function OnboardingQueuePage() {
  const queueQ = useProbearbeiterQueue()
  const createDraft = useCreateDraft()
  const navigate = useNavigate()

  const queue = queueQ.data?.rows ?? []

  const sorted = useMemo(() => [...queue].sort((a, b) => (a.ingress_at < b.ingress_at ? 1 : -1)), [queue])

  async function startBlankWizard() {
    const r = await createDraft.mutateAsync()
    navigate(`/hr/onboarding/wizard/${r.main_id}`)
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8 space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Onboarding</h1>
          <p className="text-sm text-on-surface-variant">Probearbeiter:innen prüfen oder neue Person direkt anlegen.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/hr/onboarding/probearbeiter/new">
            <Button variant="secondary">+ Probearbeiter (PL/SL)</Button>
          </Link>
          <Button variant="primary" onClick={startBlankWizard} disabled={createDraft.isPending}>
            + Neue Person (HR)
          </Button>
        </div>
      </header>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-on-surface-variant mb-3">
          Probearbeiter zur Prüfung ({sorted.length})
        </h2>
        {queueQ.isPending ? (
          <p className="text-sm text-on-surface-variant">Lade…</p>
        ) : sorted.length === 0 ? (
          <div className="rounded-2xl border border-outline-variant/40 bg-surface-container/30 p-8 text-center">
            <p className="text-on-surface-variant">Aktuell keine offenen Probearbeiten.</p>
          </div>
        ) : (
          <div className="rounded-2xl overflow-hidden border border-outline-variant/40 divide-y divide-outline-variant/30">
            {sorted.map(r => {
              const avg = avgRating(r)
              return (
                <Link
                  key={r.main_id}
                  to={`/hr/onboarding/probearbeiter/${r.main_id}`}
                  className="flex flex-wrap items-center gap-4 px-4 py-3 hover:bg-surface-container-low transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{r.vorname} {r.nachname}</div>
                    <div className="text-xs text-on-surface-variant truncate">
                      {r.probe_schule_name ?? '—'} · {r.probe_von_datum}…{r.probe_bis_datum}
                    </div>
                  </div>
                  <div className="text-sm text-amber-400">
                    {avg != null ? <>★ {avg.toFixed(1)}</> : <span className="text-on-surface-variant/50">–</span>}
                  </div>
                  <div className="text-xs text-on-surface-variant">
                    {r.probe_empfehlung === 'ja' && '✓ Ja'}
                    {r.probe_empfehlung === 'nein' && '✗ Nein'}
                    {r.probe_empfehlung === 'vielleicht' && '~ Vielleicht'}
                    {!r.probe_empfehlung && '—'}
                  </div>
                  <div className="text-xs text-on-surface-variant w-40 text-right">
                    {new Date(r.ingress_at).toLocaleString('de-DE')}
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}
